<script setup lang="ts">
import LeIconButton from "@dsk/components/IconButton/LeIconButton.vue";
import LeMinus from "@dsk/icons/LeMinus.vue";
import LePlus from "@dsk/icons/LePlus.vue";
import { computed, ref, watch } from "vue";

import type { DictsysArticle } from "@/types";

import LeArticleBodyCollapsed from "./LeArticleBodyCollapsed.vue";
import LeArticleBodyExpanded from "./LeArticleBodyExpanded.vue";
import LeArticleInflectionScheme from "./LeArticleInflectionScheme.vue";

const props = defineProps<{
  article: DictsysArticle;
  collapsible?: boolean;
  expanded?: boolean;
}>();

const isExpanded = ref<boolean>(false);

watch(
  () => props.expanded,
  (value) => {
    isExpanded.value = value ?? false;
  },
  { immediate: true }
);

const showCollapsedArticle = computed<boolean>(() => props.collapsible && !isExpanded.value);
</script>

<template>
  <div class="le-article-body" :class="{ 'le-article-body--compact': props.collapsible }">
    <LeArticleInflectionScheme
      :scheme-id="props.article.id"
      :language="props.article.sourceLanguage"
      class="le-article-body__inflection-scheme"
    />

    <div class="le-article-body__content" :class="{ 'le-article-body__content--expanded': isExpanded }">
      <LeIconButton
        v-if="props.collapsible"
        class="le-article-body__expand-toggle"
        color="primary"
        size="l"
        @click="isExpanded = !isExpanded"
      >
        <LeMinus v-if="isExpanded" />
        <LePlus v-else />
      </LeIconButton>
      <LeArticleBodyCollapsed
        v-if="showCollapsedArticle"
        class="le-article-body__collapsed-content"
        :article="props.article"
      />
      <LeArticleBodyExpanded v-else :article="props.article" />
    </div>
  </div>
</template>

<style lang="less" scoped>
.le-article-body {
  padding: 30px;

  &--compact {
    padding: 20px 17px;
  }

  &__inflection-scheme {
    margin-bottom: 38px;
  }

  &__expand-toggle {
    display: inline;
    margin-right: 16px;
  }

  &__content {
    word-break: break-word;
    hyphens: auto;

    &--expanded .le-article-body__expand-toggle {
      margin-bottom: 14px;
    }
  }

  &__collapsed-content {
    display: inline;
  }
}
</style>
